import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

// Iter XX.31 — 30-day Gully Plus free trial banner.
//
// Rendered at natural moments inside Chat.jsx (see parent's decision).
// The banner NEVER interrupts urgent flows (distress / injury / billing /
// upload troubleshooting / pending clarification) — the parent gates
// on those before mounting this component.
//
// Founder principle: this should feel like "I'd love you to experience
// the full version of Gully", never "upgrade before time runs out".
// No countdowns. No urgency. No pressure.

const DISMISS_STORAGE_KEY = "gully_trial_banner_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;     // 7 days

function _isRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return (Date.now() - ts) < DISMISS_TTL_MS;
  } catch { return false; }
}

function _markDismissed() {
  try { localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now())); }
  catch { /* noop */ }
}

/**
 * @param {Object} props
 * @param {string} props.trialSource — granular activation surface
 * @param {Function} [props.onActivated] — parent callback after success
 */
export default function TrialBanner({ trialSource, onActivated }) {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (_isRecentlyDismissed()) return;
      try {
        const { data } = await api.get("/plus/trial/eligibility");
        if (alive && data?.eligible === true) {
          setVisible(true);
          try {
            await api.post("/plus/trial/eligibility/impression", {
              event_kind: "trial_banner_impression",
              payload: { trial_source: trialSource },
            }).catch(() => {});
          } catch { /* analytics best-effort */ }
        }
      } catch { /* eligibility endpoint missing / auth error → hide */ }
    })();
    return () => { alive = false; };
  }, [trialSource]);

  if (!visible) return null;

  async function activate() {
    setBusy(true);
    try {
      await api.post("/plus/trial/eligibility/impression", {
        event_kind: "trial_activation_click",
        payload: { trial_source: trialSource },
      }).catch(() => {});
      const { data } = await api.post("/plus/trial/start", {
        source: trialSource,
      });
      toast.success("Gully Plus is on for the next 30 days.");
      setVisible(false);
      if (typeof onActivated === "function") onActivated(data);
    } catch (e) {
      // Silent failure — no urgency. The user will see the banner
      // again next time and can try again.
      toast.error("Couldn't start the trial right now. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  function dismiss() {
    _markDismissed();
    setVisible(false);
  }

  return (
    <div
      data-testid="trial-banner"
      className="mx-auto my-4 w-full max-w-2xl px-5 py-4 rounded-2xl border border-emerald-500/30 bg-emerald-950/40 backdrop-blur-sm"
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl leading-none" aria-hidden>🏏</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-neutral-200 leading-relaxed">
            You've been using Gully. I'd love you to experience everything
            Gully Plus can do.
            <span className="text-neutral-400"> Enjoy 30 days completely free.</span>
          </p>
          <ul className="mt-3 text-xs text-neutral-400 space-y-1">
            <li>✅ No credit card</li>
            <li>✅ No payment details</li>
            <li>✅ No auto-renew</li>
          </ul>
          <p className="mt-3 text-xs text-neutral-500 leading-relaxed">
            Unlock long-term coaching memory, smarter video reviews, progress
            tracking, and every Gully Plus feature.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <button
              data-testid="trial-banner-start"
              disabled={busy}
              onClick={activate}
              className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-sm font-medium"
            >
              {busy ? "Starting…" : "Start Free Trial"}
            </button>
            <button
              data-testid="trial-banner-dismiss"
              disabled={busy}
              onClick={dismiss}
              className="text-xs text-neutral-500 hover:text-neutral-300"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
